import React from "react";
import {
  Page,
  View,
  Text,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";

// Font registration
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/npm/@fontsource/roboto/files/roboto-latin-400-normal.woff",
      fontWeight: "normal",
    },
    {
      src: "https://cdn.jsdelivr.net/npm/@fontsource/roboto/files/roboto-latin-700-normal.woff",
      fontWeight: "bold",
    },
  ],
});

const categoryColors = {
  technical: { bg: "#E3F2FD", text: "#1565C0", label: "Technical" },
  tool: { bg: "#FFF3E0", text: "#E65100", label: "Tool" },
  soft: { bg: "#E8F5E9", text: "#2E7D32", label: "Soft Skill" },
};

const styles = StyleSheet.create({
  page: {
    fontFamily: "Roboto",
    paddingTop: 110,
    paddingBottom: 85,
    position: "relative",
    paddingHorizontal: 40,
  },
  logo2: {
    position: "absolute",
    top: 30,
    left: 30,
    width: 60,
  },
  upperDesign: {
    position: "absolute",
    top: -10,
    right: 0,
    width: 280,
  },
  lowerDesign: {
    position: "absolute",
    bottom: -10,
    right: 0,
    width: 300,
  },
  heading: {
    fontSize: 23,
    fontWeight: "bold",
    borderBottomWidth: 2,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
    paddingBottom: 6,
    marginBottom: 10,
  },
  subheading: {
    fontSize: 14,
    marginBottom: 18,
  },
  // Career card
  card: {
    marginBottom: 14,
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 6,
  },
  cardHeader: {
    backgroundColor: "#f57c00",
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
  },
  cardHeaderText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "bold",
  },
  // Skill row
  skillRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  skillRowLast: {
    borderBottomWidth: 0,
  },
  categoryBadge: {
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 3,
    fontSize: 7,
    fontWeight: "bold",
    width: 55,
    textAlign: "center",
  },
  skillName: {
    fontSize: 10,
    flex: 1,
    marginLeft: 8,
  },
  pageNumber_Box: {
    position: "absolute",
    bottom: 20,
    left: 10,
    width: 25,
    height: 25,
    border: "2px solid orange",
    borderRadius: 15,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    zIndex: 2,
  },
  pageNumber: {
    fontSize: 11,
    color: "#000",
    textAlign: "center",
  },
});

const SkillMapPDF = ({ data = [], assets, showPageNumber }) => {
  return (
    <Page size="A4" style={styles.page} wrap>
      <Image src={assets.upper} style={styles.upperDesign} fixed />
      <Image src={assets.lower} style={styles.lowerDesign} fixed />
      <Image src={assets.logo2} style={styles.logo2} fixed />

      <Text style={styles.heading}>
        Your Skill Map: The Abilities You'll Need
      </Text>
      <Text style={styles.subheading}>
        Each career requires specific skills to excel. Below is a skill map
        linking career pathways to essential abilities:
      </Text>

      {data.map((item, idx) => {
        const skills = (item.skills || []).slice(0, 6);
        return (
          <View key={idx} style={styles.card} wrap={false}>
            {/* Career header */}
            <View style={styles.cardHeader}>
              <Text style={styles.cardHeaderText}>
                {idx + 1}. {item.career_pathway}
              </Text>
            </View>

            {/* Skills list */}
            {skills.map((skill, i) => {
              const isObject = typeof skill === "object" && skill !== null;
              const skillName = isObject
                ? skill.name || skill.label || ""
                : skill;
              const cat = isObject ? (skill.category || "").toLowerCase() : "";
              const catStyle = categoryColors[cat] || {
                bg: "#F5F5F5",
                text: "#555",
                label: cat || "Skill",
              };
              const isLast = i === skills.length - 1;

              return (
                <View
                  key={i}
                  style={[styles.skillRow, isLast && styles.skillRowLast]}
                >
                  <Text
                    style={[
                      styles.categoryBadge,
                      { backgroundColor: catStyle.bg, color: catStyle.text },
                    ]}
                  >
                    {catStyle.label}
                  </Text>
                  <Text style={styles.skillName}>{skillName}</Text>
                </View>
              );
            })}
          </View>
        );
      })}

      <>
        {showPageNumber && (
          <View style={styles.pageNumber_Box} fixed>
            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) => `${pageNumber}`}
              fixed
            />
          </View>
        )}
      </>
    </Page>
  );
};

export default SkillMapPDF;
